import { useEffect, useState } from 'react'
import {
  loadIncoming,
  formatEuro,
  formatDueDate,
  type IncomingItem,
  type IncomingStatus,
} from '@core/incoming'
import { Card, Section } from '@design/components'
import { DocumentThumb } from '@features/documents/DocumentThumb'
import { VoicePlayer } from '@features/documents/VoicePlayer'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { VerifyScreen } from '@features/verification/VerifyScreen'
import { TriageScreen } from './TriageScreen'
import type { Space } from '@core/session'

/**
 * "Nieuw van familie" on the admin home screen.
 *
 * Every row carries a thumbnail of the actual page, and tapping it opens the
 * original full screen. That is deliberate: the amount beside it is the AI's
 * reading, and the only way to be sure is to look at the paper. Making that
 * one tap — from the overview, without navigating into anything — is what
 * stops the extracted figure from quietly becoming the truth.
 */
export function IncomingSection({
  spaces,
  refreshKey = 0,
  onCount,
  userId,
}: {
  refreshKey?: number
  onCount?: (count: number) => void
  userId: string
  spaces: Space[]
}) {
  const [items, setItems] = useState<IncomingItem[] | null>(null)
  const [viewing, setViewing] = useState<IncomingItem | null>(null)
  const [verifying, setVerifying] = useState<IncomingItem | null>(null)
  const [tick, setTick] = useState(0)
  const [triaging, setTriaging] = useState(false)

  useEffect(() => {
    let alive = true
    const load = () =>
      loadIncoming()
        .then((rows) => {
          if (!alive) return
          setItems(rows)
          onCount?.(rows.length)
        })
        .catch(() => {
          if (!alive) return
          setItems([])
          onCount?.(0)
        })
    void load()
    const timer = setInterval(load, 20000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [refreshKey, onCount, tick])

  // Sections hide when empty (rule 23) — no "0 items" row.
  if (!items || items.length === 0) return null

  return (
    <>
      <Section title="Nieuw van familie" count={items.length}>
        {/* Offered from three items up, because below that the list is the
            faster way and a full-screen mode is just ceremony. */}
        {items.length >= 3 && (
          <button
            onClick={() => setTriaging(true)}
            className="w-full mb-2 min-h-[2.75rem] rounded-xl border border-line text-sm font-medium text-muted"
          >
            Eén voor één bekijken
          </button>
        )}
        <div className="space-y-2">
          {items.map((item) => (
            <Row
              key={item.documentId}
              item={item}
              onView={() => setViewing(item)}
              onVerify={() => setVerifying(item)}
            />
          ))}
        </div>
      </Section>

      {triaging && (
        <TriageScreen
          items={items}
          userId={userId}
          spaces={spaces}
          onClose={() => {
            setTriaging(false)
            setTick((t) => t + 1)
          }}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}

      {viewing && (
        <DocumentViewer
          documentId={viewing.documentId}
          title={viewing.supplier ?? `Van ${viewing.sentBy}`}
          onClose={() => setViewing(null)}
        />
      )}

      {verifying && (
        <VerifyScreen
          documentId={verifying.documentId}
          userId={userId}
          spaces={spaces}
          onClose={() => setVerifying(null)}
          onDone={() => {
            setVerifying(null)
            setTick((t) => t + 1)
          }}
        />
      )}
    </>
  )
}

function Row({
  item,
  onView,
  onVerify,
}: {
  item: IncomingItem
  onView: () => void
  onVerify: () => void
}) {
  // Green-lane items need nothing from the admin — offering a "check this"
  // button on them would undo the whole point of the risk engine.
  const needsAttention = item.status === 'review' || item.status === 'needs_you'
  return (
    <Card>
      <div className="flex gap-3 items-center">
      <DocumentThumb documentId={item.documentId} onOpen={onView} />

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-medium truncate">
            {item.supplier ?? 'Document'}
          </p>
          {item.amountCents !== null && (
            <span className="shrink-0 tabular-nums font-medium">{formatEuro(item.amountCents)}</span>
          )}
        </div>

        <p className="text-sm text-muted truncate">
          {item.sentBy}
          {item.dueDate && ` · vervalt ${formatDueDate(item.dueDate)}`}
        </p>

        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <StatusChip status={item.status} note={item.note} />
          <button onClick={onView} className="text-sm text-muted underline underline-offset-2">
            Origineel
          </button>
          {needsAttention && (
            <button
              onClick={onVerify}
              className="text-sm font-medium text-ink underline underline-offset-2"
            >
              Nakijken
            </button>
          )}
        </div>
      </div>
      </div>

      <VoicePlayer documentId={item.documentId} />
    </Card>
  )
}

/**
 * Shape and text carry the meaning, not colour alone — the admin may be
 * colour-blind, and this has to survive a greyscale screenshot.
 */
function StatusChip({ status, note }: { status: IncomingStatus; note: string | null }) {
  const style: Record<IncomingStatus, string> = {
    ready: 'bg-ok/10 text-ok',
    review: 'bg-warn/10 text-warn',
    needs_you: 'bg-danger/10 text-danger',
    processing: 'bg-line text-muted',
  }
  const mark: Record<IncomingStatus, string> = {
    ready: '✓',
    review: '!',
    needs_you: '!',
    processing: '…',
  }
  const fallback: Record<IncomingStatus, string> = {
    ready: 'Klaar',
    review: 'Nakijken',
    needs_you: 'Vraagt jou',
    processing: 'Bezig',
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${style[status]}`}>
      {mark[status]} {note ?? fallback[status]}
    </span>
  )
}
